import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { DeveloperBoard } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  createDashboard,
  createFolder,
  dashboardDataUid,
  dashboardSettingsUid,
  dashboardStatisticsUid,
  getBasicDashboard,
  setPermission,
} from "../../endpoints/api";
import { ESPLoader } from "../../misc/esp-web-flasher";
import {
  binaryFetch,
  claimDevice,
  commit,
  createNewDevice,
  firmwareId,
  getFirmwareVersions,
  getIdentityFromDCN,
  mklfs,
  uploadFiles,
  zdmUrl,
} from "../../endpoints/configuratorApi";
import {
  isDev,
  prepareConfigJson,
  prepareNetTestJson,
  prepareParamsJson,
  prepareSensorJson,
  workspaceId,
} from "../costant";

export type ResultLog = {
  command: string;
  stdOutput: any[];
  stdError: any[];
  result: any;
};

type DeviceConfigurationProps = {
  values: any;
  handleNext: (deviceId: string) => void;
};

const DeviceConfiguration: React.FC<DeviceConfigurationProps> = ({
  values,
  handleNext,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const partitions = {
    bootloader: new ArrayBuffer(0), //os/bootloader.bin
    partitions: new ArrayBuffer(0), //os/partitions.bin
    zerynth: new ArrayBuffer(0), //os/zerynth.bin
    firmware: new ArrayBuffer(0), //firmware/firmware.bin
    otalog: new ArrayBuffer(0), //os/otalog.bin
    fs: new ArrayBuffer(0), //resources/fs.bin
  };

  const [confirmOptions, setConfirmOptions] = useState({
    open: false,
    question: "",
    onCancel: () => {
      setConfirmOptions({ ...confirmOptions, open: false });
    },
    onConfirm: () => {},
  });

  const [devicePort, setDevicePort] = useState<SerialPort | null>(null);
  const [usbDisconnected, setUsbDisconnected] = useState<boolean>(false);
  const [deviceId, setDeviceId] = useState<string>("");
  const [processError, setProcessError] = useState<string>("");
  const [result, setResult] = useState<boolean | null>(null);
  const [flashPercentage, setFlashPercentage] = useState<number>(0);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [steps, setSteps] = useState<any[]>([
    {
      label: t("checkingUsbConnection"),
      status: "pending",
    },
    {
      label: t("settingFirmwarePreferences"),
      status: "tbd",
    },
    {
      label: t("updatingTheFirmware"),
      status: "tbd",
    },
    {
      label: t("claimingDevice"),
      status: "tbd",
    },
    {
      label: t("creatingDashboard"),
      status: "tbd",
    },
  ]);

  //STEP 1: checkingUsbConnection
  const handleDeviceSelect = () => {
    setUsbDisconnected(false);
    setProcessError("");
    setResult(null);
    navigator.serial
      .requestPort({ filters: [{ usbVendorId: 0x10c4, usbProductId: 0xea60 }] })
      .then((port) => {
        console.log("port selected:", port);
        setDevicePort(port);
        createNewDevice({ name: values.serialCode, fleet: values.fleet }).then(
          (res: any) => {
            if (res && res.device && res.device.id) {
              setDeviceId(res.device.id);
              const tmp = [...steps];
              tmp[0].status = "success";
              tmp[1].status = "pending";
              setActiveStep((prev) => prev + 1);
              setSteps([...tmp]);
              handleFirmwarePreferences(port, res.device.id);
            } else {
              setProcessError(`${t("failedCreateDevice")}`);
            }
          }
        );
      })
      .catch((e) => {
        console.log("no serial port selected:", e);
      });
  };

  //STEP 2: updatingTheFirmware
  const openPort = async (port: SerialPort, baudRate: number, retries = 3) => {
    if (retries > 0) {
      await port
        .open({ baudRate: baudRate })
        .then(() => {
          console.log("serial port opened successfully");
          navigator.serial.onconnect = () => {
            console.log("connected");
          };

          navigator.serial.ondisconnect = () => {
            setUsbDisconnected(true);
            console.log("disconnected");
            setSteps([
              {
                label: t("checkingUsbConnection"),
                status: "pending",
              },
              {
                label: t("settingFirmwarePreferences"),
                status: "tbd",
              },
              {
                label: t("updatingTheFirmware"),
                status: "tbd",
              },
              {
                label: t("claimingDevice"),
                status: "tbd",
              },
              {
                label: t("creatingDashboard"),
                status: "tbd",
              },
            ]);
            setActiveStep(0);
            setDevicePort(null);
            console.log("disconnected");
          };
          return true;
        })
        .catch(async (e: any) => {
          console.log("error during port opening:", e);
          console.log("trying to close the port and reopen it again");
          await port.close();
          await openPort(port, baudRate, retries - 1);
          return false;
        });
    } else {
      setProcessError(`${t("failedToOpenPort")}`);
      console.log("retries finished. failed to open the port.");
    }
  };

  const getPartitions = useCallback(
    async (devId: string) => {
      try {
        const vers = (await getFirmwareVersions()) as any;
        const version = vers.versions[0].version || "v0.0.0";
        for (const k of Object.keys(partitions)) {
          console.log("Starting fetching firmware", k);
          if (k !== "fs") {
            // @ts-ignore
            const fileIndex: number = vers.versions[0].sources.find((s: any) =>
              s.name.includes(k)
            ).src_id;
            console.log("fileIndex", fileIndex);
            // @ts-ignore
            partitions[k] = (await binaryFetch(
              "GET",
              `${zdmUrl}/workspaces/${workspaceId}/firmwares/${firmwareId}/versions/${version}/download?source=${fileIndex}`
            )) as ArrayBuffer;
          }
        }

        console.log("before mklfsService");
        const resp = await mklfs({
          files: {
            "zfs/net.json": JSON.stringify(values.configuration),
            "zfs/params.json": JSON.stringify(prepareParamsJson(values.robby)),
            "zfs/sensors.json": JSON.stringify(prepareSensorJson),
            "zfs/config.json": JSON.stringify(prepareConfigJson),
            "zfs/net_test.json": JSON.stringify(prepareNetTestJson),
          },
        });
        if (!resp) {
          setProcessError(`${t("failedMklfs")}`);
          console.log("failed to mklfs");
          return "";
        } else {
          console.log("RESP", resp);
          partitions["fs"] = resp as ArrayBuffer;
          console.log("after mklfsService");
          //zfs
          const netFile = {
            file: new File(
              [
                new TextEncoder().encode(JSON.stringify(values.configuration)),
              ] as BlobPart[],
              "net.json"
            ),
          };
          const paramsFile = {
            file: new File(
              [
                new TextEncoder().encode(
                  JSON.stringify(prepareParamsJson(values.robby))
                ),
              ] as BlobPart[],
              "params.json"
            ),
          };
          const sensorsFile = {
            file: new File(
              [
                new TextEncoder().encode(JSON.stringify(prepareSensorJson)),
              ] as BlobPart[],
              "sensors.json"
            ),
          };
          const configFile = {
            file: new File(
              [
                new TextEncoder().encode(JSON.stringify(prepareConfigJson)),
              ] as BlobPart[],
              "config.json"
            ),
          };
          const netTestFile = {
            file: new File(
              [
                new TextEncoder().encode(JSON.stringify(prepareNetTestJson)),
              ] as BlobPart[],
              "net_test.json"
            ),
          };
          await uploadFiles(devId, [
            netFile,
            paramsFile,
            sensorsFile,
            configFile,
            netTestFile,
          ]).then(async (res: any) => {
            if (res && res.files) {
              await commit(devId, true);
            }
          });
          return devId;
        }
      } catch (e) {
        setProcessError(String(e));
        console.log(e);
        devicePort?.close();
      }
    },
    [steps, deviceId]
  );

  const flash = useCallback(
    async (devicePort: any, deviceId: string) => {
      console.log("before get partitions");
      const devId = await getPartitions(deviceId);
      console.log("after get partitions");
      console.log("DEVID", devId);
      const tmp = [...steps];
      tmp[1].status = "success";
      tmp[2].status = "pending";
      setActiveStep((prev) => prev + 1);
      setSteps([...tmp]);
      try {
        console.log("DP", devicePort);
        if (devicePort) {
          await openPort(devicePort, 115200);

          const loader = new ESPLoader(devicePort, {
            log: (...args) => console.log(...args),
            debug: (...args) => console.log(...args),
            error: (...args) => console.log(...args),
          });
          console.log("loader initializer", loader);
          try {
            await loader.initialize();
          } catch (error) {
            console.log("loader initialize error", error);
          }
          console.log("loader runstub");
          const espStub = await loader.runStub();
          console.log("setbaudrate");
          await espStub.setBaudrate(921600);
          console.log("PARTITIONS", partitions);
          const ps = [
            {
              name: "bootloader",
              data: partitions.bootloader,
              offset: 0x1000,
            },
            {
              name: "partitions",
              data: partitions.partitions,
              offset: 0x9000,
            },
            {
              name: "otalog",
              data: partitions.otalog,
              offset: 0x910000,
            },
            {
              name: "zerynth",
              data: partitions.zerynth,
              offset: 0x10000,
            },
            {
              name: "firmware",
              data: partitions.firmware,
              offset: 0x210000,
            },
            {
              name: "fs",
              data: partitions.fs,
              offset: 0x920000,
            },
          ];

          for (const p of ps) {
            const i = ps.indexOf(p);
            await espStub.flashData(
              p.data,
              (bytesWritten, totalBytes) => {
                setFlashPercentage(
                  i * (100 / ps.length) +
                    (100 / ps.length) * (bytesWritten / totalBytes)
                );
              },
              p.offset
            );
          }

          console.log("stub disconnect");
          await espStub.hardReset();
          await espStub.disconnect();
          await devicePort.close();
          console.log("device flashed successfully");
          console.log("DEVID: ", devId);
          return devId;
        } else {
          setProcessError(`${t("noDevicePort")}`);
          console.log("NO DEVICE PORT");
        }
      } catch (e) {
        if (devicePort) {
          devicePort?.close();
        }
        setProcessError(t("failedFlashFirmware") + ": " + String(e));
        console.log("failed to flash firmware", e);
        return "";
      }
      return devId;
    },
    [steps, deviceId]
  );

  const handleFirmwarePreferences = useCallback(
    (port: any, deviceId: string) => {
      flash(port, deviceId).then(async (res) => {
        console.log("RES FLASH: ", res);
        if (res) {
          console.log("device flashed successfully");
          const tmp = [...steps];
          tmp[2].status = "success";
          tmp[3].status = "pending";
          setActiveStep((prev) => prev + 1);
          setSteps([...tmp]);
          await handleClaimingDevice(port, res);
        } else {
          setProcessError(`${t("failedFlashFirmware")}`);
        }
      });
    },
    [steps, deviceId]
  );

  //STEP 3: claimingDevice
  const handleClaimingDevice = useCallback(
    async (devicePort: SerialPort, devId: string) => {
      console.log("Starting claim procedure. Device Port: ", devicePort);
      await openPort(devicePort, 115200);
      console.log("port opened");
      // Read answer
      const textDecoder = new TextDecoderStream();
      const readableStreamClosed = devicePort.readable.pipeTo(
        textDecoder.writable
      );
      const reader = textDecoder.readable.getReader();
      console.log("reader created");
      let line = "";
      // Ask bundle to device
      const enc = new TextEncoder();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (isDev) {
          break;
        } else {
          console.log("reading");
          const { value, done } = await reader.read();
          if (done) {
            reader.releaseLock();
            break;
          }
          const nonce = Math.floor(new Date().getTime() / 1000);
          line += value;
          if (value && value.includes("\n")) {
            console.log(line);
            if (line.includes("pong")) {
              if (devicePort) {
                const writer = devicePort.writable.getWriter();
                const data = enc.encode(`bundle ${nonce}\n`);
                await writer.write(data);
                writer.releaseLock();
              }
            } else if (line.includes(":")) {
              const bundle = line.replaceAll("\n", "").replaceAll("#", "");
              const parts = bundle.split(":");
              try {
                const b1 = window.atob(parts[0]);
                const b1o = JSON.parse(b1);
                // If the first part of the decoded bundle contains the same nonce => this is the bundle
                // Workaround: sometimes the nonce received from the firmware is x-1
                if (b1o.Nonce === `${nonce}` || b1o.Nonce === `${nonce - 1}`) {
                  try {
                    console.log("bundle received ", devId);
                    const res = (await getIdentityFromDCN(b1o.DCN)) as any;
                    if (res && res.identity) {
                      if (res.identity.device_id !== devId) {
                        // Device already claimed by another device
                        setProcessError(
                          `${t("deviceAlreadyClaimed")} ${
                            res.identity.device_id
                          }. ${t("shouldUnclaim")}.`
                        );
                        console.log(
                          "device already claimed (another device id)"
                        );
                      } else {
                        const tmp = [...steps];
                        tmp[3].status = "success";
                        tmp[4].status = "pending";
                        setActiveStep((prev) => prev + 1);
                        setSteps([...tmp]);
                        console.log("device already claimed (this device id)");
                      }
                      const loader = new ESPLoader(devicePort, {
                        log: (...args) => console.log(...args),
                        debug: (...args) => console.log(...args),
                        error: (...args) => console.log(...args),
                      });
                      const espStub = await loader.runStub();
                      console.log("setbaudrate");
                      await espStub.setBaudrate(460800);
                      console.log("hardreset after claim");
                      await espStub.hardReset();
                      await espStub.disconnect();
                      await reader.cancel();
                      await readableStreamClosed.catch(() => {});

                      const writer = devicePort.writable.getWriter();
                      const data = enc.encode(`exit\n`);
                      await writer.write(data);
                      writer.releaseLock();
                      break;
                    } else {
                      const res = (await claimDevice(
                        devId,
                        //b1o.DCN,
                        bundle
                      )) as any;
                      if (res && res.identity) {
                        const tmp = [...steps];
                        tmp[3].status = "success";
                        tmp[4].status = "pending";
                        setActiveStep((prev) => prev + 1);
                        setSteps([...tmp]);
                        console.log("device claimed successfully");
                        await reader.cancel();
                        await readableStreamClosed.catch(() => {});
                        const writer = devicePort.writable.getWriter();
                        const data = enc.encode(`exit\n`);
                        await writer.write(data);
                        writer.releaseLock();
                        console.log(
                          "Sim not claimed beacasue not present in configuration"
                        );
                        addMachineDashboard(devId);
                        break;
                      } else {
                        if (
                          res.message &&
                          (res.message.includes("could be counterfeited") ||
                            res.message.includes("is unknown"))
                        ) {
                          const tmp = [...steps];
                          tmp[2].status = "error";
                          setActiveStep((prev) => prev + 1);
                          setSteps([...tmp]);
                          setProcessError("Error: " + res.message);
                          await reader.cancel();
                          await readableStreamClosed.catch(() => {});
                          const loader = new ESPLoader(devicePort, {
                            log: (...args: any) => console.log(...args),
                            debug: (...args: any) => console.log(...args),
                            error: (...args: any) => console.log(...args),
                          });
                          const espStub = await loader.runStub();
                          console.log("setbaudrate");
                          await espStub.setBaudrate(921600);
                          console.log("hardreset after claim");
                          await espStub.hardReset();
                          await espStub.disconnect();
                          break;
                        } else {
                          // TODO: handle other error responses
                        }
                      }
                    }
                  } catch (e) {
                    console.log("catch", e);
                    break;
                  } finally {
                    await reader.cancel();
                    await readableStreamClosed.catch(() => {});
                    if (devicePort) {
                      devicePort?.close();
                    }
                  }
                }
              } catch {
                console.log("failed to parse bundle");
              }
            } else if (line.includes("Inactivity timeout")) {
              console.log("timeout");
            } else if (line.includes("#")) {
              setTimeout(async () => {
                if (devicePort) {
                  const writer = devicePort.writable.getWriter();
                  const data = enc.encode(`ping\n`);
                  await writer.write(data);
                  writer.releaseLock();
                }
              }, 2000);
            }
            line = "";
          }
        }
      }
      if (isDev) {
        const tmp = [...steps];
        tmp[3].status = "success";
        tmp[4].status = "pending";
        setActiveStep((prev) => prev + 1);
        setSteps([...tmp]);
        console.log("TESTLOCAL - device claimed successfully");
        addMachineDashboard(devId);
      }
    },
    [deviceId, steps]
  );

  //STEP 4: creatingDashboard
  const addMachineDashboard = async (devId: string) => {
    const rawData1: any = await getBasicDashboard(dashboardDataUid);
    const rawData2: any = await getBasicDashboard(dashboardSettingsUid);
    const rawData3: any = await getBasicDashboard(dashboardStatisticsUid);
    if (
      rawData1 &&
      rawData1.dashboard &&
      rawData2 &&
      rawData2.dashboard &&
      rawData3 &&
      rawData3.dashboard
    ) {
      createFolder(values.serialCode).then((f: any) => {
        if (f && f.id) {
          const folderId = f.id;

          // Funzione per gestire ogni dashboard
          const setupDashboard = async (rawData: any, titlePrefix: string) => {
            // Aggiornamento dei valori di "asset_id" e "asset_name"
            for (let i = 0; i < (rawData?.templating?.list || []).length; i++) {
              if (rawData.templating.list[i].name === "asset_id") {
                rawData.templating.list[i].query = devId;
              }
              if (rawData.templating.list[i].name === "asset_name") {
                rawData.templating.list[i].query = values.name;
              }
            }
            rawData.title = `${titlePrefix} - ${values.serialCode}`;

            // Aggiornamento tag
            rawData.tags = [values.serialCode];
            rawData.links = [
              {
                asDropdown: true,
                icon: "external link",
                includeVars: true,
                keepTime: true,
                tags: [values.serialCode],
                targetBlank: true,
                title: `${values.serialCode}`,
                tooltip: "",
                type: "dashboards",
                url: "",
              },
            ];

            // Creazione dashboard e impostazione permessi
            const res: any = await createDashboard(rawData, folderId);
            if (res && res.id) {
              const permissionRes: any = await setPermission(
                res.id,
                values.userId
              );
              if (
                permissionRes &&
                permissionRes.message === "Dashboard permissions updated"
              ) {
                return true;
              } else {
                setProcessError("Failed to set dashboard permission");
                return false;
              }
            } else {
              setProcessError("Failed to create new dashboard");
              return false;
            }
          };

          // Esecuzione delle promise in parallelo
          Promise.all([
            setupDashboard(rawData1.dashboard, "DATA"),
            setupDashboard(rawData2.dashboard, "SETTINGS"),
            setupDashboard(rawData3.dashboard, "STATISTICS"),
          ])
            .then((results) => {
              if (results.every((result) => result === true)) {
                setResult(true);
              } else {
                setResult(false);
                setProcessError("An error occurred during dashboard setup");
              }
            })
            .catch((error) => {
              setResult(false);
              setProcessError(
                "An error occurred during dashboard setup " + error
              );
            });
        } else {
          setResult(false);
          setProcessError("Failed to create new client folder");
        }
      });
    } else {
      setResult(false);
      setProcessError("Failed to create new dashboard");
    }
  };

  useEffect(() => {
    handleDeviceSelect();
  }, []);

  return (
    <Grid
      container
      spacing={2}
      direction="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      <Grid item>
        <DeveloperBoard style={{ fontSize: "64px" }} />
      </Grid>
      <Grid item>
        <Typography variant="h5">{t("deviceConfiguration")}</Typography>
      </Grid>
      {result === null && (
        <Grid item>
          <Typography>{`${t("waitDescription")}`}</Typography>
        </Grid>
      )}
      {usbDisconnected ? (
        <Grid
          container
          item
          spacing={4}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs={12} md={8} xl={6}>
            <Alert
              icon={false}
              severity="error"
              style={{
                marginTop: "5%",
                borderRadius: "8px",
                padding: "16px",
                fontSize: "1.1rem",
                backgroundColor: "#fce4e4",
                color: "#b71c1c",
                border: "1px solid #f44336",
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                textAlign: "left",
              }}
            >
              <AlertTitle style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                Errore: Disconnessione USB ⚠️
              </AlertTitle>
              <span style={{ marginTop: "8px", lineHeight: "1.5" }}>
                <strong>Azione richiesta:</strong> per continuare la
                configurazione ricollega il dispositivo USB e premi il bottone
                sottostante.
              </span>
            </Alert>
            <Button
              variant="contained"
              onClick={() => {
                handleDeviceSelect();
              }}
              style={{ marginTop: "16px" }}
            >
              {t("retry")}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          item
          spacing={4}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs={12} md={8} xl={6}>
            <Grid item container spacing={2} direction="column">
              {!result && (
                <Grid item>
                  <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    style={{ paddingTop: "16px" }}
                  >
                    {steps.map(
                      (
                        step: { label: string; status: string },
                        index: number
                      ) => (
                        <Step key={step.label}>
                          <StepLabel>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item>
                                <Typography variant="h6">
                                  {step.label}
                                </Typography>
                              </Grid>
                              {step.status === "pending" && (
                                <Grid item>
                                  <CircularProgress size={25} />
                                </Grid>
                              )}
                              <Grid item xs />
                              {step.status === "pending" && index === 0 && (
                                <Grid item>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                      handleDeviceSelect();
                                    }}
                                  >
                                    {t("selectDevice")}
                                  </Button>
                                </Grid>
                              )}
                              {step.status === "pending" && index === 2 && (
                                <Grid item>{flashPercentage.toFixed(2)}%</Grid>
                              )}
                            </Grid>
                          </StepLabel>
                        </Step>
                      )
                    )}
                  </Stepper>
                </Grid>
              )}
              {processError && (
                <Grid item>
                  <Alert severity="error">
                    <AlertTitle>{t("error")}</AlertTitle>
                    {processError}
                  </Alert>
                </Grid>
              )}
              {result === true && (
                <Grid item>
                  <Alert
                    severity="success"
                    style={{
                      marginTop: "5%",
                      borderRadius: "8px",
                      padding: "16px",
                      fontSize: "1.1rem",
                      backgroundColor: "#eaf6e8",
                      color: "#256029",
                      border: "1px solid #4CAF50",
                    }}
                  >
                    <AlertTitle style={{ fontWeight: "bold" }}>
                      Configurazione completata con successo!
                    </AlertTitle>
                    L’operazione è stata eseguita correttamente.
                  </Alert>
                </Grid>
              )}
              {(result === true || result === false) && (
                <Grid item container spacing={2} justifyContent="center">
                  <Grid item>
                    <Button
                      size="large"
                      variant="contained"
                      onClick={() => navigate(0)}
                      style={{ marginTop: "32px" }}
                    >
                      {t("otherDeviceConfiguration")}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default DeviceConfiguration;

/*
  const firmware_download = (result: string) => {
    console.log(sendChannels.firmware_download);
    if (result !== "") {
      path = result;
      window.api.send(
        sendChannels.ztc_change_connection,
        values.robby && Object.keys(values.robby).length > 0
          ? [values.configuration, values.robby]
          : [values.configuration]
      );
    } else {
      setResult(false);
      clearInterval(interval);
    }
  };

  const ztc_change_connection = (result: boolean, lines: any) => {
    console.log(sendChannels.ztc_change_connection, result, lines);
    if (result) {
      if (remainingSeconds !== tenPercentRemainingSeconds) {
        setRemainingSeconds(tenPercentRemainingSeconds);
      }
      setPercent(10);
      window.api.send(sendChannels.ztc_discover, "");
    } else {
      setResult(false);
      clearInterval(interval);
    }
  };

  const ztc_discover = (result: ResultLog) => {
    console.log("DISCOVER: ", result);
    const devices: any[] = result.result;
    board = "";
    uid = "";
    if (devices.length > 0) {
      let tmp_board: string | null = null;
      let tmp_uid: string | null = null;
      devices.map((el) => {
        if (!board && !uid) {
          tmp_board = el.board;
          tmp_uid = el.uid;
        }
      });
      if (!tmp_board || !tmp_uid) {
        setResult(false);
        setLastLog(result);
        clearInterval(interval);
      } else {
        board = tmp_board;
        uid = tmp_uid;
        setPercent(20);
        if (remainingSeconds !== twentyPercentRemainingSeconds) {
          setRemainingSeconds(twentyPercentRemainingSeconds);
        }
        window.api.send(sendChannels.ztc_erase, [board, uid]);
      }
    } else {
      setResult(false);
      setLastLog(result);
      clearInterval(interval);
    }
  };

  const ztc_erase = (result: ResultLog) => {
    console.log("ERASE: ", result);
    if (result.result) {
      setPercent(30);
      if (remainingSeconds !== thirtyPercentRemainingSeconds) {
        setRemainingSeconds(thirtyPercentRemainingSeconds);
      }
      window.api.send(sendChannels.ztc_phys_id, uid);
    } else {
      setResult(false);
      setLastLog(result);
      clearInterval(interval);
    }
  };

  const ztc_phys_id = async (result: ResultLog) => {
    console.log("GETTING PHYSID: ", result);
    await window.api.send(sendChannels.ztc_check_discover, [board, uid]);
    if (checkDevice) {
      if (board === "4zerobox_v9") {
        window.api.send(sendChannels.ztc_provision_prepare, [
          `/firmwares/provisioning`,
          board,
          uid,
        ]);
      } else {
        phys_id = String(result.result);
        window.api.send(sendChannels.ztc_provision_prepare, [
          `/firmwares/provisioning`,
          board,
          phys_id,
        ]);
      }
      setPercent(40);
      if (remainingSeconds !== fortyPercentRemainingSeconds) {
        setRemainingSeconds(fortyPercentRemainingSeconds);
      }
    } else {
      setResult(false);
      setLastLog(result);
      clearInterval(interval);
    }
  };

  const ztc_provision_prepare = async (result: ResultLog) => {
    console.log("PROVISION PREPARE: ", result);
    await window.api.send(sendChannels.ztc_check_discover, [board, uid]);
    if (checkDevice) {
      if (Boolean(result.result)) {
        setPercent(60);
        if (remainingSeconds !== sixtyPercentRemainingSeconds) {
          setRemainingSeconds(sixtyPercentRemainingSeconds);
        }
        if (board === "4zerobox_v9") {
          window.api.send(sendChannels.ztc_provision_command, uid);
        } else {
          window.api.send(sendChannels.ztc_provision_command, phys_id);
        }
      } else {
        setResult(false);
        setLastLog(result);
        clearInterval(interval);
      }
    } else {
      setResult(false);
      setLastLog(result);
      clearInterval(interval);
    }
  };

  const ztc_provision_command = async (result: ResultLog) => {
    console.log("PROVISION COMMAND: ", result);
    bundle = String(result.result);
    await window.api.send(sendChannels.ztc_check_discover, [board, uid]);
    if (checkDevice) {
      setPercent(70);
      if (remainingSeconds !== seventyPercentRemainingSeconds) {
        setRemainingSeconds(seventyPercentRemainingSeconds);
      }
      await window.api.send(sendChannels.zdm_device_create, [
        workspaceId,
        values.fleetId,
        values.name.split(" ").join("_"),
      ]);
    } else {
      setResult(false);
      setLastLog(result);
      clearInterval(interval);
    }
  };

  const zdm_device_create = async (result: ResultLog) => {
    console.log("DEVICE CREATE: ", result);
    if (result.result) {
      deviceId = String(result.result);
      setPercent(75);
      if (remainingSeconds !== eightyPercentRemainingSeconds) {
        setRemainingSeconds(seventyfivePercentRemainingSeconds);
      }
      await window.api.send(sendChannels.zdm_device_identity, [
        workspaceId,
        String(result.result),
        bundle,
        phys_id,
      ]);
    } else {
      setResult(false);
      setLastLog(result);
      clearInterval(interval);
    }
  };

  const zdm_device_identity = async (result: ResultLog) => {
    console.log("DEVICE IDENTITY: ", result);
    if (result.result) {
      setPercent(80);
      if (remainingSeconds !== eightyPercentRemainingSeconds) {
        setRemainingSeconds(eightyPercentRemainingSeconds);
      }
      window.api.send(sendChannels.ztc_burn, [board, uid, path]);
    } else {
      setResult(false);
      setLastLog(result);
      clearInterval(interval);
    }
  };

  const ztc_burn = async (result: ResultLog) => {
    console.log("BURN: ", result);
    await window.api.send(sendChannels.ztc_check_discover, [board, uid]);
    if (checkDevice) {
      if (
        result.result.length > 0 &&
        !String(result.result).includes("error")
      ) {
        setPercent(90);
        if (remainingSeconds !== hundredPercentRemainingSeconds) {
          setRemainingSeconds(hundredPercentRemainingSeconds);
        }
        addMachineDashboard();
        // setResult(true);
      } else {
        setResult(false);
        setLastLog(result);
        clearInterval(interval);
      }
    } else {
      setResult(false);
      clearInterval(interval);
    }
  };
*/
